import React, { useMemo } from 'react'
import { Checkbox, TimePicker } from 'antd'
import dayjs from 'dayjs'
import _ from 'lodash'
import styled from 'styled-components'

import { colours } from '@qflow/theme'

import { AlertRecipients } from './AlertRecipients'
import { ALERT_RULE_TITLE_MAPPER, DEFAULT_TIME_RANGE } from './constants'
import {
  AdditionalText,
  AlertRuleContainer,
  Description,
  SettingArea,
  TextArea
} from './sharedStyledComponents'
import { EnableAlertRuleSwitch } from './EnableSwitch'

export const LogisticsWindow = ({
  projectId,
  alertKey,
  alertRules,
  setAlertRules,
  cannotEnable
}: {
  projectId: string
  alertKey: string
  alertRules: any
  setAlertRules: (alertRules: any) => void
  cannotEnable: boolean
}) => {
  if (!alertRules[alertKey]) {
    return null
  }

  return (
    <>
      <AlertRuleContainer>
        <TextArea>
          <Description>{ALERT_RULE_TITLE_MAPPER[alertKey]}</Description>
          <AdditionalText>
            Set the permitted days and times that deliveries/collections can be
            made to this project.
          </AdditionalText>
          <AdditionalText>
            An alert will be triggered if a movement is recorded outside of the
            defined window.
          </AdditionalText>
        </TextArea>
        <SettingArea>
          <EnableAlertRuleSwitch
            alertKey={alertKey}
            alertRules={alertRules}
            setAlertRules={setAlertRules}
            cannotEnable={cannotEnable}
            ariaLabel="Enable Logistics Window"
          />
          <TimeRangeContainer>
            <TimeRangeContainerText>
              Define your logistics window
            </TimeRangeContainerText>
            <DayComponent
              day={'monday'}
              alertKey={alertKey}
              alertRules={alertRules}
              setAlertRules={setAlertRules}
            />
            <DayComponent
              day={'tuesday'}
              alertKey={alertKey}
              alertRules={alertRules}
              setAlertRules={setAlertRules}
            />
            <DayComponent
              day={'wednesday'}
              alertKey={alertKey}
              alertRules={alertRules}
              setAlertRules={setAlertRules}
            />
            <DayComponent
              day={'thursday'}
              alertKey={alertKey}
              alertRules={alertRules}
              setAlertRules={setAlertRules}
            />
            <DayComponent
              day={'friday'}
              alertKey={alertKey}
              alertRules={alertRules}
              setAlertRules={setAlertRules}
            />
            <DayComponent
              day={'saturday'}
              alertKey={alertKey}
              alertRules={alertRules}
              setAlertRules={setAlertRules}
            />
            <DayComponent
              day={'sunday'}
              alertKey={alertKey}
              alertRules={alertRules}
              setAlertRules={setAlertRules}
            />
          </TimeRangeContainer>
        </SettingArea>
      </AlertRuleContainer>
      <AlertRecipients
        projectId={projectId}
        alertKey={alertKey}
        alertRules={alertRules}
        setAlertRules={setAlertRules}
      />
    </>
  )
}

// Day Component
const DayComponent = ({
  day,
  alertKey,
  alertRules,
  setAlertRules
}: {
  day: string
  alertKey: string
  alertRules: any
  setAlertRules: (alertRules: any) => void
}) => {
  const presetsFromAlertRule = useMemo(() => {
    const presetAlert: { label: string; value: [dayjs.Dayjs, dayjs.Dayjs] }[] =
      [
        {
          label: '08:00-18:00',
          value: [dayjs('08:00', 'HH:mm'), dayjs('18:00', 'HH:mm')]
        },
        {
          label: '09:00-17:00',
          value: [dayjs('09:00', 'HH:mm'), dayjs('17:00', 'HH:mm')]
        }
      ]

    Object.entries(alertRules[alertKey]?.schedule).forEach(([key, value]) => {
      if (
        value &&
        typeof value === 'object' &&
        'start' in value &&
        'end' in value &&
        value.start &&
        value.end &&
        typeof value.start === 'string' &&
        typeof value.end === 'string' &&
        !presetAlert.some(
          preset => preset.label === `${value.start}-${value.end}`
        ) &&
        !(value.start === '00:00' && value.end === '23:59')
      ) {
        presetAlert.push({
          label: `${value.start}-${value.end}`,
          value: [dayjs(value.start, 'HH:mm'), dayjs(value.end, 'HH:mm')]
        })
      }

      return presetAlert
    })

    presetAlert.sort((a, b) => a.label.localeCompare(b.label))
    presetAlert.unshift({
      label: 'All day',
      value: [dayjs('00:00', 'HH:mm'), dayjs('23:59', 'HH:mm')]
    })
    return presetAlert
  }, [alertRules, alertKey])

  if (!alertRules[alertKey]?.schedule) {
    return null
  }

  return (
    <DayRow $disabled={!alertRules[alertKey]?.schedule[day].isAllowed}>
      <Checkbox
        checked={alertRules[alertKey]?.schedule[day].isAllowed}
        onChange={() => {
          const updatedAlertRules = _.cloneDeep(alertRules)
          updatedAlertRules[alertKey].schedule[day].isAllowed =
            !alertRules[alertKey].schedule[day].isAllowed

          if (!updatedAlertRules[alertKey].schedule[day].isAllowed) {
            updatedAlertRules[alertKey].schedule[day].start = null
            updatedAlertRules[alertKey].schedule[day].end = null
          } else {
            updatedAlertRules[alertKey].schedule[day].start =
              DEFAULT_TIME_RANGE.start
            updatedAlertRules[alertKey].schedule[day].end =
              DEFAULT_TIME_RANGE.end
          }

          setAlertRules(updatedAlertRules)
        }}
      >
        <DayText>{day}</DayText>
      </Checkbox>
      <TimePicker.RangePicker
        format={'HH:mm'}
        allowClear={false}
        allowEmpty={[
          !alertRules[alertKey].schedule[day].isAllowed,
          !alertRules[alertKey].schedule[day].isAllowed
        ]}
        disabled={!alertRules[alertKey].schedule[day].isAllowed}
        minuteStep={5}
        placeholder={
          !alertRules[alertKey].schedule[day].isAllowed
            ? ['Not permitted', '']
            : undefined
        }
        presets={presetsFromAlertRule}
        value={
          alertRules[alertKey].schedule[day].isAllowed
            ? [
                dayjs(alertRules[alertKey].schedule[day].start, 'HH:mm'),
                dayjs(alertRules[alertKey].schedule[day].end, 'HH:mm')
              ]
            : undefined
        }
        onChange={(_, dateString) => {
          const updatedAlertRules = {
            ...alertRules
          }
          updatedAlertRules[alertKey].schedule[day].start = dateString[0]
          updatedAlertRules[alertKey].schedule[day].end = dateString[1]
          setAlertRules(updatedAlertRules)
        }}
      />
    </DayRow>
  )
}

// Styled Components
const TimeRangeContainer = styled.div`
  background-color: ${colours.WHITE};
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  border-radius: 0.5rem;
  border: 1px solid ${colours.CLOUD_GREY};
  padding: 0.75rem;
  margin-top: 0.25rem;
  min-width: 442px;
`
const TimeRangeContainerText = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
`
const DayRow = styled.div<{ $disabled: boolean }>`
  display: flex;
  align-items: center;
  font-size: 0.875rem;

  .ant-picker-range-separator {
    opacity: ${({ $disabled }) => ($disabled ? 0 : 1)};
`
const DayText = styled.div`
  width: 5.5rem;
  text-transform: capitalize;
`
